import React, { useEffect, useState } from "react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash2, X } from "lucide-react";

const emptyForm = {
    code: "",
    discount_pct: 10,
    discount_max: 100,
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10),
    usage_limit: 1000,
    active: true,
};

const PromoCodes = () => {
    const [codes, setCodes] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [err, setErr] = useState("");

    const load = () => api.get("/promo-codes").then((r) => setCodes(r.data));
    useEffect(() => { load(); }, []);

    const handleCreate = async () => {
        setErr("");
        try {
            await api.post("/promo-codes", {
                code: form.code,
                discount_pct: parseFloat(form.discount_pct),
                discount_max: parseFloat(form.discount_max),
                valid_from: new Date(form.valid_from).toISOString(),
                valid_to: new Date(form.valid_to).toISOString(),
                usage_limit: parseInt(form.usage_limit),
                active: !!form.active,
            });
            setShowForm(false);
            setForm(emptyForm);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this promo code?")) return;
        await api.delete(`/promo-codes/${id}`);
        await load();
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Promo Codes"
                subtitle={`${codes.length} promo codes`}
                action={
                    <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold" data-testid="promo-new-btn">
                        <Plus className="w-4 h-4" /> New promo
                    </button>
                }
            />

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <th className="px-4 py-3">Code</th>
                                <th className="px-4 py-3 text-right">Discount</th>
                                <th className="px-4 py-3 text-right">Max ₹</th>
                                <th className="px-4 py-3">Valid</th>
                                <th className="px-4 py-3 text-right">Used / Limit</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {codes.map((c) => (
                                <tr key={c.id} className="hover:bg-slate-50" data-testid={`promo-row-${c.code}`}>
                                    <td className="px-4 py-3 font-mono font-semibold text-navy-900">{c.code}</td>
                                    <td className="px-4 py-3 text-right">{c.discount_pct}%</td>
                                    <td className="px-4 py-3 text-right">₹{c.discount_max}</td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(c.valid_from)} → {formatDate(c.valid_to)}</td>
                                    <td className="px-4 py-3 text-right tabular-nums">{c.used} / {c.usage_limit}</td>
                                    <td className="px-4 py-3">
                                        {c.active ? <span className="text-xs font-semibold text-mint-700 bg-mint-50 px-2 py-0.5 rounded-full border border-mint-200">Active</span> : <span className="text-xs text-slate-400">Disabled</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg" data-testid={`promo-delete-${c.code}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="promo-form">
                        <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="promo-form-close"><X className="w-5 h-5" /></button>
                        <h3 className="font-display font-bold text-xl text-navy-900 mb-4">Create promo code</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Code</label>
                                <input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SUMMER25" className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg uppercase font-mono" data-testid="promo-form-code" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Discount %</label>
                                    <input type="number" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: e.target.value})} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="promo-form-pct" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Max ₹</label>
                                    <input type="number" value={form.discount_max} onChange={(e) => setForm({...form, discount_max: e.target.value})} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="promo-form-max" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Valid from</label>
                                    <input type="date" value={form.valid_from} onChange={(e) => setForm({...form, valid_from: e.target.value})} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Valid to</label>
                                    <input type="date" value={form.valid_to} onChange={(e) => setForm({...form, valid_to: e.target.value})} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Usage limit</label>
                                    <input type="number" value={form.usage_limit} onChange={(e) => setForm({...form, usage_limit: e.target.value})} className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" data-testid="promo-form-limit" />
                                </div>
                            </div>
                            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
                            <button onClick={handleCreate} className="w-full py-2.5 rounded-full bg-mint-500 hover:bg-mint-600 text-white font-semibold text-sm" data-testid="promo-form-submit">Create</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromoCodes;
